import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Container } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import BookingTableSection from "./BookingTableSection";
import CommonButton from "../../../components/commonButton";
import FilterModal from "../../../components/FilterModal/FilterModal";
import { Upload04Icon, TotalItemsIcon, PublishedIcon, RejectIcon, ClockIcon, InputRupeeIcon } from "../../../assets/icons";
import { useToast } from "../../../context/TostContext";
import { usePermission } from "../../../hooks/usePermission";
import { fetchAdminMarketplaceBookings, fetchAdminBookingStats, exportAdminMarketplaceBookings } from "../../../redux/slice/marketplaceSlice";
import { TabCard } from "../../../components/custom-card/Cards";
import { FilterBtn } from "../../../components/commonButton/btns";
import { TableSearch } from "../../../components/form/Fields";

const MODULE_SLUG = "marketplace_bookings";

export default function BookingList() {
    const dispatch = useDispatch();
    const { hasPermission } = usePermission();
    const toast = useToast();
    const canExport = hasPermission(MODULE_SLUG, "export");

    const headerPortalTarget = document.getElementById("header-portal-target");

    const headerContent = (
        <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="fw-semibold mb-0 text-primary fs-lg">
                <span className="text-muted fw-normal">Marketplace / </span>Bookings
            </h4>
        </div>
    );

    const {
        adminMarketplaceBookings,
        adminMarketplaceBookingPagination,
        adminMarketplaceBookingStats,
        isBookingLoading,
        isInitialLoad
    } = useSelector((state) => state.marketplace);

    const [listingCurrentPage, setListingCurrentPage] = useState(1);
    const [listingPerPage, setListingPerPage] = useState({ value: 10, label: 10 });
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [filterValues, setFilterValues] = useState({ status: "" });
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const statusOptions = [
        { value: "pending", label: "Pending" },
        { value: "confirmed", label: "Confirmed" },
        { value: "cancelled", label: "Cancelled" },
        { value: "failed", label: "Failed" }
    ];

    const fetchList = useCallback((page = listingCurrentPage, limit = listingPerPage.value, search = debouncedSearch, status = filterValues.status) => {
        dispatch(fetchAdminMarketplaceBookings({ page, limit, search, status }))
            .unwrap()
            .catch((error) => {
                toast.error(error || "Failed to fetch bookings");
            });
    }, [dispatch, listingCurrentPage, listingPerPage, debouncedSearch, filterValues.status, toast]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setListingCurrentPage(1);
        }, 800);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        fetchList(listingCurrentPage, listingPerPage.value, debouncedSearch, filterValues.status);
    }, [fetchList, listingCurrentPage, listingPerPage, debouncedSearch, filterValues.status]);

    useEffect(() => {
        dispatch(fetchAdminBookingStats());
    }, [dispatch]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const isFilterActive = !!filterValues.status;
    const totalItems = adminMarketplaceBookingPagination?.total || 0;

    const bookingStatsCards = [
        {
            id: 1,
            total: adminMarketplaceBookingStats?.total || 0,
            title: "Total Bookings",
            icon: <TotalItemsIcon width={20} height={20} />,
            status: "",
            iconClassName: "icon-secondary"
        },
        {
            id: 2,
            total: adminMarketplaceBookingStats?.pending || 0,
            title: "Pending",
            icon: <ClockIcon size={20} />,
            status: "pending",
            iconClassName: "icon-warning"
        },
        {
            id: 3,
            total: adminMarketplaceBookingStats?.confirmed || 0,
            title: "Confirmed",
            icon: <PublishedIcon width={20} height={20} />,
            status: "confirmed",
            iconClassName: "icon-success"
        },
        {
            id: 4,
            total: adminMarketplaceBookingStats?.cancelled || 0,
            title: "Cancelled",
            icon: <RejectIcon width={20} height={20} />,
            status: "cancelled",
            iconClassName: "icon-danger"
        },
        {
            id: 5,
            total: adminMarketplaceBookingStats?.failed || 0,
            title: "Failed",
            icon: <RejectIcon width={20} height={20} />,
            status: "failed",
            iconClassName: "icon-danger"
        },
        {
            id: 6,
            total: `₹${(adminMarketplaceBookingStats?.revenue || 0).toLocaleString()}`,
            title: "Total Revenue",
            icon: <InputRupeeIcon size={20} />,
            status: "revenue",
            iconClassName: "icon-success"
        },
    ];

    return (
        <React.Fragment>
            {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
            <Container fluid className="px-0">
                <div className="grid gap-4 mb-4">
                    {bookingStatsCards.map((card) => (
                        <TabCard
                            key={card.id}
                            title={card.title}
                            count={card.total}
                            icon={card.icon}
                            status={card.status}
                            iconClassName={card.iconClassName}
                            className="g-col-2"
                        />
                    ))}
                </div>
                <div className="bg-white rounded-2 overflow-hidden common-shadow">
                    {/* Header Section */}
                    <div className="p-4 d-flex align-items-center justify-content-between gap-4">
                        <div className="d-flex align-items-center gap-4">

                            <TableSearch value={searchTerm}
                                placeholder="Search Bookings"
                                onChange={handleSearch} />
                            <FilterBtn onClick={() => setShowFilterModal(true)} active={isFilterActive} count={1} />

                        </div>

                        <div className="action-buttons-container">
                            {canExport && (
                                <CommonButton
                                    icon={<Upload04Icon strokeWidth="2.0" />}
                                    variant="secondary"
                                    title={isExporting ? "Exporting..." : "Export"}
                                    type="button"
                                    disabled={isExporting}
                                    isLoading={isExporting}
                                    onClick={() => {
                                        setIsExporting(true);
                                        dispatch(exportAdminMarketplaceBookings({
                                            search: debouncedSearch,
                                            status: filterValues.status
                                        }))
                                            .unwrap()
                                            .then(() => {
                                                toast.success("Export successful");
                                            })
                                            .catch((err) => {
                                                toast.error(err || "Export failed");
                                            })
                                            .finally(() => {
                                                setIsExporting(false);
                                            });
                                    }}
                                />
                            )}
                        </div>
                    </div>

                    <BookingTableSection
                        data={adminMarketplaceBookings}
                        isLoading={isBookingLoading}
                        isInitialLoad={isInitialLoad}
                        currentPage={listingCurrentPage}
                        setCurrentPage={setListingCurrentPage}
                        pageSize={listingPerPage.value}
                        setPageSize={(limit) => {
                            setListingPerPage({ value: limit, label: limit });
                            setListingCurrentPage(1);
                        }}
                        totalItems={totalItems}
                    />
                </div>
            </Container>

            <FilterModal
                isOpen={showFilterModal}
                onClose={() => setShowFilterModal(false)}
                title="Filters"
                size="lg"
                filterValues={filterValues}
                setFilterValues={setFilterValues}
                setCurrentPage={setListingCurrentPage}
                statusOptions={statusOptions}
                onClear={() => {
                    setFilterValues({ status: "" });
                    setListingCurrentPage(1);
                    setShowFilterModal(false);
                }}
            />
        </React.Fragment>
    );
}

